import { IconProps } from '@components/icons/icon-props.type';
import { Spinner } from '@components/ui//Spinner/Spinner';
import { Ripple } from '@components/ui/Ripple/Ripple';
import {
  Button as HeadlessButton,
  ButtonProps as HeadlessButtonProps
} from '@headlessui/react';
import {
  type ElementType,
  ForwardedRef,
  forwardRef,
  JSXElementConstructor,
  PropsWithChildren,
  ReactNode,
  Ref,
  RefAttributes
} from 'react';
import { VariantProps } from 'tailwind-variants';
import { buttonStyles } from './Button.style';

type ButtonVariants = VariantProps<typeof buttonStyles>;
type ButtonVariant = ButtonVariants['variant'];
type ButtonSize = ButtonVariants['size'];
type ButtonColor = ButtonVariants['color'];

type ButtonRenderProps = {
  variant?: ButtonVariant;
  color?: ButtonColor;
  size?: ButtonSize;
  rounded?: boolean;
  flat?: boolean;
  tile?: boolean;
  icon?: boolean;
  prependIcon?: JSXElementConstructor<IconProps>;
  appendIcon?: JSXElementConstructor<IconProps>;
};

export type ButtonProps<E extends ElementType = 'button'> = PropsWithChildren<
  {
    className?: string;
    loading?: boolean;
    loadingText?: string;
  } & Omit<HeadlessButtonProps<E>, 'color'> &
    ButtonRenderProps
>;

const loaderStrokeMapper: Record<NonNullable<ButtonSize>, number> = {
  xs: 2,
  sm: 3,
  md: 3,
  lg: 4,
  xl: 5
};

function fixedForwardRef<T, P = NonNullable<unknown>>(
  render: (props: P, ref: Ref<T>) => ReactNode
): (props: P & RefAttributes<T>) => ReactNode {
  return forwardRef(render as any) as any;
}

const Button_ = <E extends ElementType>(
  {
    as,
    variant,
    color,
    size,
    prependIcon: PrependIcon,
    appendIcon: AppendIcon,
    rounded,
    flat,
    tile,
    className,
    loading,
    loadingText,
    children,
    ...props
  }: ButtonProps<E>,
  ref: ForwardedRef<HTMLElement>
) => {
  const buttonClassNames = buttonStyles({
    color: color,
    variant: variant,
    size: size,
    rounded: rounded,
    flat: flat,
    tile: tile,
    appended: !!AppendIcon,
    prepended: !!PrependIcon
  });

  return (
    <HeadlessButton
      as={as}
      {...(props as any)}
      ref={ref}
      className={buttonClassNames.base({ className })}
      data-ui-disabled={props.disabled || undefined}
      data-loading={loading || undefined}
      disabled={props.disabled || loading}
    >
      <>
        {PrependIcon ? (
          <PrependIcon className={buttonClassNames.icon()} />
        ) : null}

        <div className={buttonClassNames.text()}>{children}</div>

        {AppendIcon ? <AppendIcon className={buttonClassNames.icon()} /> : null}
      </>

      {loading ? (
        <div className={buttonClassNames.loaderWrapper()}>
          <Spinner
            strokeWidth={size && loaderStrokeMapper[size]}
            className={buttonClassNames.loader()}
            theme="light"
          />
          {loadingText ? <div>{loadingText}</div> : null}
        </div>
      ) : null}

      {/* Hover Overlay */}
      {!props.disabled ? <div className={buttonClassNames.overlay()} /> : null}

      {/* Click Ripple Effect */}
      {!props.disabled && !loading ? (
        <Ripple spanClassName={buttonClassNames.ripple()} />
      ) : null}
    </HeadlessButton>
  );
};

export const Button = fixedForwardRef(Button_);
